import { useTranslation } from 'react-i18next';

import useLinkAfterCreate from 'src/Components/EntityLinks/hooks/useLinkAfterCreate';
import { ExpenseForm } from 'src/Components/Forms/Expense/ExpenseForm';
import { EXPENSE_TYPE_TREE, MainCategory } from 'src/Constants/expenseCategorization';
import {
  CreateExpenseFormSplitScope,
  useCreateExpenseForm,
} from 'src/Hooks/Forms/useCreateExpenseForm';
import { usePayerError } from 'src/Hooks/Forms/usePayerError';
import { useCreateExpense } from 'src/Hooks/Mutations/useCreateExpense';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import {
  trackExpenseCreated,
  trackExpenseSplitByHousehold,
  trackRecurrenceSet,
} from 'src/Services/Mixpanel/events';

import { Props } from './ExpenseCreationFormWrapper.types';

const ExpenseCreationFormWrapper = ({ onCancel, onCreate, defaultIsIncome }: Props) => {
  const { form } = useCreateExpenseForm({ defaultIsIncome });
  const { handleCreated } = useLinkAfterCreate(LinkableEntityType.Expense);
  const handlePayerError = usePayerError(form);
  const { mutate } = useCreateExpense({
    onSuccess: data => handleCreated(data.id, onCreate),
    onError: handlePayerError,
  });
  const { t } = useTranslation();

  return (
    <ExpenseForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(values => {
        if (form.values.splitScope === CreateExpenseFormSplitScope.HOUSEHOLD) {
          trackExpenseSplitByHousehold({
            split_type: form.values.splitType,
            households_count: form.values.householdShares.filter(s => s.selected).length,
            total_amount: form.values.amount ?? 0,
          });
        }
        trackExpenseCreated({
          is_income: form.values.isIncome,
          is_fund_call:
            !!form.values.type &&
            EXPENSE_TYPE_TREE[form.values.type]?.main === MainCategory.FundingCalls,
          fund_call_payer_set: !!form.values.fundCallPayerId,
        });
        // operation_type is what tells us whether the recurrence on incomes unlocked by
        // TA3107-14 actually gets used.
        if (values.recurrence) {
          trackRecurrenceSet({
            operation_type: form.values.isIncome ? 'income' : 'expense',
            frequency: values.recurrence.frequency,
          });
        }
        mutate(values);
      })}
      submitButtonLabel={
        form.values.isIncome
          ? t('expense.form.income.add', 'Enregistrer la recette')
          : t('expense.form.add', 'Enregistrer la dépense')
      }
    />
  );
};

export default ExpenseCreationFormWrapper;
